import { useState, useEffect } from 'react';
import { useAuth, apiService } from '../AdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { 
  Plus, Pencil, Trash2, Save, Search, RefreshCw, MoreVertical,
  Star, CheckCircle, XCircle, Pin, Video, User, Briefcase, Play
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { ImageUploader } from '../../components/ui/ImageUploader';

const sources = ['Google', 'Facebook', 'WhatsApp', 'Direct', 'Website'];
const videoTypes = ['youtube', 'instagram', 'direct'];

const defaultReview = {
  id: '', name: '', position: '', rating: 5, review: '', short_quote: '', source: 'Google',
  customer_image: '', video_url: '', video_type: '', is_approved: true, is_featured: false, 
  is_video_testimonial: false, show_on_homepage: true, date: ''
};

export const ReviewsManager = () => {
  const { token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterApproved, setFilterApproved] = useState('all');
  const [editModal, setEditModal] = useState({ open: false, item: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });

  useEffect(() => { fetchReviews(); }, []);

  const fetchReviews = async () => {
    try {
      const res = await apiService.getReviews(token);
      setReviews(res.data);
    } catch (error) {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const { item } = editModal;
    if (!item.id || !item.name || !item.review) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (item.isNew) {
        await apiService.createReview(token, item);
        toast.success('Review created successfully!');
      } else {
        await apiService.updateReview(token, item.id, item);
        toast.success('Review updated successfully!');
      }
      setEditModal({ open: false, item: null });
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving review');
    }
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteReview(token, deleteDialog.item.id);
      toast.success('Review deleted');
      setDeleteDialog({ open: false, item: null });
      fetchReviews();
    } catch (error) {
      toast.error('Error deleting review');
    }
  };

  const toggleApproval = async (review) => {
    try {
      await apiService.updateReview(token, review.id, { ...review, is_approved: !review.is_approved });
      toast.success(review.is_approved ? 'Review hidden' : 'Review approved');
      fetchReviews();
    } catch (error) {
      toast.error('Error updating review');
    }
  };

  const toggleFeatured = async (review) => {
    try {
      await apiService.updateReview(token, review.id, { ...review, is_featured: !review.is_featured });
      toast.success(review.is_featured ? 'Unpinned from featured' : 'Pinned as featured');
      fetchReviews();
    } catch (error) {
      toast.error('Error updating review');
    }
  };

  const openEditModal = (item = null) => {
    setEditModal({
      open: true,
      item: item ? { ...item, isNew: false } : { ...defaultReview, id: `review-${Date.now()}`, date: new Date().toISOString().split('T')[0], isNew: true }
    });
  };

  const filtered = reviews.filter(r => {
    const matchSearch = r.name.toLowerCase().includes(search.toLowerCase()) || r.review.toLowerCase().includes(search.toLowerCase());
    const matchApproved = filterApproved === 'all' || (filterApproved === 'approved' ? r.is_approved : !r.is_approved);
    return matchSearch && matchApproved;
  });

  const stats = {
    total: reviews.length,
    approved: reviews.filter(r => r.is_approved).length,
    featured: reviews.filter(r => r.is_featured).length,
    avgRating: reviews.length ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1) : 0
  };

  return (
    <div className="space-y-6" data-testid="reviews-manager">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Oswald'] text-2xl font-bold text-slate-900">Reviews / Testimonials</h1>
          <p className="text-slate-500">Manage customer reviews and testimonials</p>
        </div>
        <Button onClick={() => openEditModal()} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="w-4 h-4 mr-2" /> Add Review
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center"><Star className="w-5 h-5 text-sky-600" /></div>
            <div><p className="text-2xl font-bold">{stats.total}</p><p className="text-xs text-slate-500">Total Reviews</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><CheckCircle className="w-5 h-5 text-green-600" /></div>
            <div><p className="text-2xl font-bold">{stats.approved}</p><p className="text-xs text-slate-500">Approved</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center"><Pin className="w-5 h-5 text-orange-600" /></div>
            <div><p className="text-2xl font-bold">{stats.featured}</p><p className="text-xs text-slate-500">Featured</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center"><Star className="w-5 h-5 text-yellow-600 fill-yellow-500" /></div>
            <div><p className="text-2xl font-bold">{stats.avgRating}</p><p className="text-xs text-slate-500">Avg Rating</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search reviews..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={filterApproved} onValueChange={setFilterApproved}>
              <SelectTrigger className="w-full md:w-48"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Reviews</SelectItem>
                <SelectItem value="approved">Approved Only</SelectItem>
                <SelectItem value="pending">Pending Approval</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchReviews}><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Reviews List */}
      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <Star className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No reviews found</p>
          <Button onClick={() => openEditModal()} variant="outline" className="mt-4"><Plus className="w-4 h-4 mr-2" /> Add Review</Button>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((review) => (
            <Card key={review.id} className={`${!review.is_approved ? 'opacity-60 border-dashed' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    {review.customer_image ? (
                      <img src={review.customer_image} alt={review.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-sky-600 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                        {review.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-900">{review.name}</h3>
                        {review.position && <span className="text-xs text-slate-500">{review.position}</span>}
                        <span className="text-xs text-slate-400">{review.source}</span>
                        {review.is_video_testimonial && <span className="px-2 py-0.5 bg-pink-100 text-pink-700 text-xs rounded flex items-center gap-1"><Video className="w-3 h-3" /> Video</span>}
                        {review.is_featured && <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">Featured</span>}
                        {!review.is_approved && <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">Pending</span>}
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}`} />
                        ))}
                        {review.date && <span className="text-xs text-slate-400 ml-2">{review.date}</span>}
                      </div>
                      <p className="text-slate-600 mt-2 italic">"{review.short_quote || review.review}"</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="icon" variant={review.is_featured ? 'default' : 'outline'} className={`w-8 h-8 ${review.is_featured ? 'bg-orange-500' : ''}`} onClick={() => toggleFeatured(review)} title="Pin as featured">
                      <Pin className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant={review.is_approved ? 'default' : 'outline'} className={`w-8 h-8 ${review.is_approved ? 'bg-green-500' : ''}`} onClick={() => toggleApproval(review)} title={review.is_approved ? 'Hide review' : 'Approve review'}>
                      {review.is_approved ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="w-8 h-8"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditModal(review)}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDeleteDialog({ open: true, item: review })} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, item: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editModal.item?.isNew ? 'Add New Review' : 'Edit Review'}</DialogTitle>
            <DialogDescription>Fill in the review details below</DialogDescription>
          </DialogHeader>
          
          {editModal.item && (
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Review ID</Label>
                  <Input value={editModal.item.id} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Source</Label>
                  <Select value={editModal.item.source} onValueChange={(value) => setEditModal({ ...editModal, item: { ...editModal.item, source: value } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{sources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><User className="w-4 h-4" /> Customer Name *</Label>
                  <Input value={editModal.item.name} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, name: e.target.value } })} placeholder="Customer's name" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Briefcase className="w-4 h-4" /> Position/Company</Label>
                  <Input value={editModal.item.position || ''} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, position: e.target.value } })} placeholder="Manager, ABC Company" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Rating *</Label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button key={star} type="button" onClick={() => setEditModal({ ...editModal, item: { ...editModal.item, rating: star } })} className="focus:outline-none">
                      <Star className={`w-8 h-8 transition-colors ${star <= editModal.item.rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300 hover:text-yellow-200'}`} />
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Short Quote (for display)</Label>
                <Input value={editModal.item.short_quote || ''} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, short_quote: e.target.value } })} placeholder="This service has transformed our operations!" maxLength={150} />
                <p className="text-xs text-slate-500">Short impactful quote for cards (max 150 chars)</p>
              </div>

              <div className="space-y-2">
                <Label>Full Review Text *</Label>
                <Textarea value={editModal.item.review} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, review: e.target.value } })} placeholder="Customer's full review..." rows={4} />
              </div>

              {/* Customer Image */}
              <ImageUploader
                label="Customer Photo (Optional)"
                value={editModal.item.customer_image || ''}
                onChange={(url) => setEditModal({ ...editModal, item: { ...editModal.item, customer_image: url } })}
                allowFreeCrop={true}
                token={token}
              />

              {/* Video Testimonial Section */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Video className="w-5 h-5 text-pink-500" />
                  <Label className="font-semibold">Video Testimonial</Label>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Video URL</Label>
                    <Input 
                      value={editModal.item.video_url || ''} 
                      onChange={(e) => {
                        const url = e.target.value;
                        let video_type = '';
                        if (url.includes('youtube.com') || url.includes('youtu.be')) video_type = 'youtube';
                        else if (url.includes('instagram.com')) video_type = 'instagram';
                        else if (url) video_type = 'direct';
                        setEditModal({ ...editModal, item: { ...editModal.item, video_url: url, video_type, is_video_testimonial: !!url } });
                      }} 
                      placeholder="https://youtube.com/watch?v=..." 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Video Type</Label>
                    <Select value={editModal.item.video_type || ''} onValueChange={(value) => setEditModal({ ...editModal, item: { ...editModal.item, video_type: value } })}>
                      <SelectTrigger><SelectValue placeholder="Auto-detect" /></SelectTrigger>
                      <SelectContent>
                        {videoTypes.map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Switch 
                    checked={editModal.item.is_video_testimonial || false} 
                    onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, is_video_testimonial: checked } })} 
                  />
                  <Label className="flex items-center gap-1"><Play className="w-4 h-4" /> Mark as Video Testimonial</Label>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Date (Optional)</Label>
                <Input type="date" value={editModal.item.date || ''} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, date: e.target.value } })} />
              </div>

              <div className="flex flex-wrap gap-6 pt-2">
                <div className="flex items-center gap-2">
                  <Switch checked={editModal.item.is_approved} onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, is_approved: checked } })} />
                  <Label>Approved</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={editModal.item.is_featured} onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, is_featured: checked } })} />
                  <Label>Featured</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={editModal.item.show_on_homepage !== false} onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, show_on_homepage: checked } })} />
                  <Label>Show on Homepage</Label>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, item: null })}>Cancel</Button>
            <Button onClick={handleSave} className="bg-sky-500 hover:bg-sky-600"><Save className="w-4 h-4 mr-2" /> Save Review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Review?</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this review from {deleteDialog.item?.name}? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ReviewsManager;
